import { Text, Flex, Icon, Loader } from '@vibe/core';
import { Inbox, Update, LogIn, LogOut } from '@vibe/icons';
import { type FrontendAnalyticsData } from '../services/analytics';
import { useItem } from '../hooks/use-items';

// This assumes it gets a ready item.
type UseItemReturn = ReturnType<typeof useItem>;
type ItemData = NonNullable<UseItemReturn['item']>;

function Item({ item }: { item: ItemData }) {
  return <span>{item.name}</span>;
}

function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp + 'Z'); // treat as UTC
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16).replace('T', ' ');
}

function LoginActivityEvent({ activityEvent }: { activityEvent: FrontendAnalyticsData['recentActivityLog'][0] }) {
  const { item: clientItem, isLoading, error } = useItem(activityEvent.client_item_id);

  if (error) {
    return (
      <Text type="text2" color="primary" style={{ flex: 1 }}>
        <strong>Error:</strong> No data found for client <strong>{activityEvent.client_item_id}</strong>.
      </Text>
    );
  }

  if (isLoading || clientItem === undefined) {
    return (
      <Flex style={{ flex: 1 }}>
        <Loader size={13} />
      </Flex>
    );
  }

  if (clientItem === null) {
    return (
      <Text type="text2" color="primary" style={{ flex: 1 }}>
        <strong>Error:</strong> Could not find client <strong>{activityEvent.client_item_id}</strong>.
      </Text>
    );
  }

  return (
    <Text type="text2" color="primary" style={{ flex: 1 }}>
      Client{' '}
      <strong>
        <Item item={clientItem} />
      </strong>{' '}
      logged into the portal.
    </Text>
  );
}

function CommentActivityEvent({ activityEvent }: { activityEvent: FrontendAnalyticsData['recentActivityLog'][0] }) {
  // parse the itemId from activityEvent.path ( /portal/item/:itemId/comments )
  const pathParts = activityEvent.path.split('/');
  const itemId = parseInt(pathParts[3], 10);

  const client = useItem(activityEvent.client_item_id);
  const target = useItem(itemId);

  const anyError = client.error || target.error;
  const anyLoading = client.isLoading || target.isLoading || client.item === undefined || target.item === undefined;
  const anyNotFound = client.item === null || target.item === null;

  if (anyError) {
    return (
      <Text type="text2" color="primary" style={{ flex: 1 }}>
        <strong>Error:</strong> No data found for client <strong>{activityEvent.client_item_id}</strong> or item <strong>{itemId}</strong>.
      </Text>
    );
  }

  if (anyLoading) {
    return (
      <Flex style={{ flex: 1 }}>
        <Loader size={13} />
      </Flex>
    );
  }

  if (anyNotFound) {
    return (
      <Text type="text2" color="primary" style={{ flex: 1 }}>
        <strong>Error:</strong> Could not find client <strong>{activityEvent.client_item_id}</strong> or item <strong>{itemId}</strong>.
      </Text>
    );
  }

  return (
    <Text type="text2" color="primary" style={{ flex: 1 }}>
      Client{' '}
      <strong>
        <Item item={client.item as ItemData} />
      </strong>{' '}
      commented on item{' '}
      <strong>
        <Item item={target.item as ItemData} />
      </strong>
      .
    </Text>
  );
}

type ActivityLogProps = {
  recentActivityLog: FrontendAnalyticsData['recentActivityLog'];
  selectedEventType?: 'all' | 'auth' | 'comment';
};

export function ActivityLog({ recentActivityLog, selectedEventType = 'all' }: ActivityLogProps) {
  if (!recentActivityLog) {
    return null;
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        marginTop: '5px',
        height: 'calc(100% - 26px)',
        backgroundColor: '#fcfcfcff',
        border: '1px solid #d0d4e4',
        borderRadius: '4px',
      }}
    >
      {recentActivityLog.length === 0 ||
      (selectedEventType !== 'all' && !recentActivityLog.some((event) => event.type === selectedEventType)) ? (
        <Flex
          gap={8}
          direction="column"
          align="center"
          justify="center"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#fcfcfcff',
            color: '#7e7f93ff',
          }}
        >
          <Icon icon={Inbox} size={24} useCurrentColor />
          <Text type="text2" color="secondary">
            No data available
          </Text>
        </Flex>
      ) : (
        recentActivityLog.map((activityEvent, index) =>
          selectedEventType === 'all' || selectedEventType === activityEvent.type ? (
            <div
              key={`${activityEvent.client_item_id}-${activityEvent.timestamp}-${index}`}
              data-type={activityEvent.type}
              style={{
                padding: '10px 14px',
              }}
              className="dashboard__client--item"
            >
              <Flex direction="row" align="stretch" gap={16}>
                <Text type="text2" color="secondary" ellipsis={false} style={{ minWidth: '115px' }}>
                  {formatTimestamp(activityEvent.timestamp)}
                </Text>
                {activityEvent.type === 'auth' && (
                  <>
                    <LoginActivityEvent activityEvent={activityEvent} />
                  </>
                )}
                {activityEvent.type === 'comment' && (
                  <>
                    <CommentActivityEvent activityEvent={activityEvent} />
                  </>
                )}
              </Flex>
            </div>
          ) : null,
        )
      )}
    </div>
  );
}
